// BooksManagement.jsx
import React, { useState, useEffect, useCallback } from "react";
import * as api from "../services/api";
import { SCHOOL_CLASSES, CLASS_BOOKS_MAP } from "../contexts/constants";
import Card from "../components/Card";
import Spinner from "../components/Spinner";
import Modal from "../components/Modal";
import Button from "../components/Button";
import Input from "../components/Input";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  PlusIcon,
  PencilSquareIcon,
  ArrowUpOnSquareIcon,
  TrashIcon,
  ArchiveBoxXMarkIcon,
  XCircleIcon,
} from "@heroicons/react/24/outline";
const StockCard = ({ stock, onAddQuantity, onExport, onDelete }) => (
  <div className="bg-white rounded-xl shadow-md overflow-hidden ring-1 ring-slate-200 transition-all hover:shadow-lg hover:-translate-y-1">
    <div className="p-4 bg-slate-50 border-b flex justify-between items-center">
      <h3 className="text-xl font-bold text-slate-900">{`الصف - ${stock.className}`}</h3>

      <div className="flex gap-1">

        {/* إضافة كميات */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onAddQuantity}
          title="إضافة كميات"
        >
          <PencilSquareIcon className="h-6 w-6 text-blue-500 hover:text-blue-700" />
        </Button>

        {/* إخراج كتب */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onExport}
          title="إخراج كميات"
        >
          <ArrowUpOnSquareIcon className="h-6 w-6 text-yellow-500 hover:text-yellow-700" />
        </Button>

        {/* حذف المخزون */}
        <Button
          variant="ghost"
          size="icon"
          onClick={onDelete}
          title="حذف المخزون"
        >
          <TrashIcon className="h-6 w-6 text-red-500 hover:text-red-700" />
        </Button>

      </div>
    </div>

    <div className="p-4">
      <table className="w-full text-md">
        <thead>
          <tr className="border-b text-lg text-slate-900">
            <th className="py-2 px-3 text-right font-semibold">الكتاب</th>
            <th className="py-2 px-3 text-center font-semibold">الكمية المتوفرة</th>
          </tr>
        </thead>

        <tbody>
          {stock.books && stock.books.length > 0 ? (
            stock.books.map((book, i) => (
              <tr key={i} className="border-b last:border-none hover:bg-gray-50">
                <td className="py-3 px-3 text-lg text-slate-800">{book.name}</td>
                <td className="py-3 px-3 text-center font-mono font-bold text-lg text-blue-700">
                  {book.quantity}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="2" className="py-4 text-center text-gray-500 italic">
                لا توجد كتب في هذا المخزون
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  </div>
);

const EmptyState = ({ onAdd }) => (
  <div className="text-center py-16 px-6 border-2 border-dashed border-gray-300 rounded-lg">
    <ArchiveBoxXMarkIcon className="mx-auto h-12 w-12 text-gray-400" />
    <h3 className="mt-2 text-lg font-medium text-gray-900">لا يوجد مخزون كتب مسجل</h3>
    <p className="mt-1 text-sm text-gray-500">ابدأ بإضافة أول مخزون كتب للمدرسة.</p>
    <div className="mt-6">
      <Button onClick={onAdd} className="bg-green-600 hover:bg-green-700 flex items-center gap-2 mx-auto">
        <PlusIcon className="h-5 w-5" />
        إضافة مخزون جديد
      </Button>
    </div>
  </div>
);

const BooksManagement = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [className, setClassName] = useState("");
  // books holds objects like { name: string, quantity: string } (string for controlled input)
  const [books, setBooks] = useState([{ name: "", quantity: "" }]);
  const [selectedStockId, setSelectedStockId] = useState(null);

  // memoized fetch
  const fetchStocks = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.getBookStocks();
      setStocks(data || []);
    } catch (err) {
      console.error(err);
      setError("فشل في جلب بيانات مخزون الكتب");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStocks();
  }, [fetchStocks]);

  const resetForm = () => {
    setClassName("");
    setBooks([{ name: "", quantity: "" }]);
    setSelectedStockId(null);
  };

  const handleBookChange = (index, field, value) => {
    setBooks(prev => {
      const copy = [...prev];
      copy[index] = { ...copy[index], [field]: value };
      return copy;
    });
  };

  const handleAddBookField = () => setBooks(prev => [...prev, { name: "", quantity: "" }]);

  const handleRemoveBookField = (index) => {
    if (books.length <= 1) {
      toast.info("يجب أن يوجد كتاب واحد على الأقل.");
      return;
    }
    setBooks(prev => prev.filter((_, i) => i !== index));
  };

  const handleClassNameChange = (e) => {
    const selectedClass = e.target.value;
    setClassName(selectedClass);
    if (selectedClass && CLASS_BOOKS_MAP[selectedClass]) {
      const predefinedBooks = CLASS_BOOKS_MAP[selectedClass].map(bookName => ({
        name: bookName,
        quantity: ""
      }));
      setBooks(predefinedBooks.length > 0 ? predefinedBooks : [{ name: "", quantity: "" }]);
    } else {
      setBooks([{ name: "", quantity: "" }]);
    }
  };

  // When opening edit/export modals, populate books from selected stock (prefill names)
  useEffect(() => {
    if (!selectedStockId) return;

    const stock = stocks.find(s => s._id === selectedStockId);
    if (!stock) return;

    // Prefill book names from current stock, leave quantities blank for user input.
    const prefilled = (stock.books || []).map(b => ({
      name: b.name || "",
      quantity: "" // user will enter the added/exported qty
    }));
    setClassName(stock.className || "");
    setBooks(prefilled.length > 0 ? prefilled : [{ name: "", quantity: "" }]);
  }, [selectedStockId, stocks]);

  // API handlers - refresh immediately after success
  const handleAddStock = async (e) => {
    e.preventDefault();
    const payload = {
      className,
      books: books.map(b => ({ name: b.name, quantity: Number(b.quantity) || 0 })),
    };

    try {
      await api.addBookStock(payload);
      resetForm();
      setIsAddModalOpen(false);
      toast.success("✅ تم إضافة المخزون بنجاح");
      await fetchStocks();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "فشل في إضافة المخزون");
    }
  };

  const handleEditStock = async (e) => {
    e.preventDefault();
    if (!selectedStockId) {
      toast.error("لم يتم تحديد المخزون للتعديل");
      return;
    }

    const payload = {
      booksToAdd: books.map(b => ({ name: b.name, quantity: Number(b.quantity) || 0 })),
    };

    try {
      await api.updateBookStock(selectedStockId, payload);
      resetForm();
      setIsEditModalOpen(false);
      toast.success("✅ تم تحديث الكميات بنجاح");
      await fetchStocks();
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "فشل في تحديث الكميات");
    }
  };

const handleExportStock = async (e) => {
  e.preventDefault();
  if (!selectedStockId) {
    toast.error("لم يتم تحديد المخزون للإخراج");
    return;
  }

  const booksToExport = books
    .map(b => ({ name: b.name, quantity: Number(b.quantity) || 0 }))
    .filter(b => b.quantity > 0); // ✅ only include those with valid quantity

  if (booksToExport.length === 0) {
    toast.info("الرجاء إدخال كمية واحدة على الأقل لإخراجها.");
    return;
  }

  try {
    await api.exportBooks(selectedStockId, { booksToExport });
    resetForm();
    setIsExportModalOpen(false);
    toast.success("✅ تم إخراج الكتب بنجاح");
    await fetchStocks();
  } catch (err) {
    console.error(err);
    toast.error(err?.response?.data?.message || "فشل في إخراج الكتب");
  }
};


  const handleDeleteStock = async (stockId) => {
    if (!window.confirm("هل أنت متأكد من حذف مخزون هذا الصف؟ سيتم حذف جميع الكتب والكميات المرتبطة به بشكل نهائي.")) return;
    try {
      await api.deleteBookStock(stockId);
      toast.success("🗑️ تم حذف المخزون بنجاح");
      await fetchStocks();
    } catch (err) {
      console.error(err);
      toast.error("فشل في حذف المخزون");
    }
  };

  return (
    <div className="space-y-6">

      <Card title="إدارة مخزون الكتب" titleClass="text-2xl font-bold text-slate-800">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
          <p className="text-md text-slate-800">عرض وتعديل وإضافة مخزون الكتب للصفوف الدراسية.</p>
          <Button
            onClick={() => {
              resetForm();
              setIsAddModalOpen(true);
            }}
            className="bg-green-600 hover:bg-green-700 flex items-center gap-2 w-full md:w-auto"
          >
            <PlusIcon className="h-5 w-5" />
            إضافة مخزون صف جديد
          </Button>
        </div>

        {loading && <div className="flex justify-center p-10"><Spinner /></div>}
        {!loading && error && <p className="text-red-500 text-center py-10">{error}</p>}
        {!loading && !error && (
          stocks.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {[...stocks]
             .sort((a, b) => SCHOOL_CLASSES.indexOf(a.className) - SCHOOL_CLASSES.indexOf(b.className))
             .map(stock => (
                <StockCard
                   key={stock._id}
                    stock={stock}
                   onAddQuantity={() => { setSelectedStockId(stock._id); setIsEditModalOpen(true); }}
                   onExport={() => { setSelectedStockId(stock._id); setIsExportModalOpen(true); }}
                     onDelete={() => handleDeleteStock(stock._id)}
                         />
                             ))}

            </div>
          ) : <EmptyState onAdd={() => { resetForm(); setIsAddModalOpen(true); }} />
        )}
      </Card>

      {/* Add Stock Modal */}
      <Modal isOpen={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="➕ إضافة مخزون جديد">
        <form onSubmit={handleAddStock} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">الصف الدراسي</label>
            <select value={className} onChange={handleClassNameChange} required className="border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 rounded-md shadow-sm w-full p-2 bg-white">
              <option value="">اختر الصف</option>
              {SCHOOL_CLASSES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <p className="text-xs text-gray-500 mt-1">سيتم ملء قائمة الكتب تلقائياً عند اختيار الصف.</p>
          </div>

          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">الكتب والكميات</label>
            {books.map((book, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Input label="اسم الكتاب" value={book.name} onChange={(e) => handleBookChange(i, "name", e.target.value)} required />
                <Input label="الكمية" type="number" value={book.quantity} onChange={(e) => handleBookChange(i, "quantity", e.target.value)} />
                <button type="button" onClick={() => handleRemoveBookField(i)} className="mb-1 text-gray-400 hover:text-red-500 transition-colors" aria-label="Remove Item"><XCircleIcon className="h-6 w-6" /></button>
              </div>
            ))}
          </div>

          <Button type="button" onClick={handleAddBookField} variant="ghost" className="text-blue-600 flex items-center gap-1"><PlusIcon className="h-4 w-4" /> إضافة كتاب آخر</Button>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)}>إلغاء</Button>
            <Button type="submit" className="bg-green-600 hover:bg-green-700">حفظ</Button>
          </div>
        </form>
      </Modal>

      {/* Edit (Add Quantity) Stock Modal */}
      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="✏️ إضافة كميات كتب">
        <form onSubmit={handleEditStock} className="space-y-4">
          <p className="text-sm text-gray-600">أدخل الكتب والكميات التي تريد إضافتها إلى المخزون الحالي. يمكنك إضافة كتب جديدة أو زيادة كمية الكتب الموجودة.</p>
          <div className="space-y-3">
            {books.map((book, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Input label="اسم الكتاب" value={book.name} onChange={(e) => handleBookChange(i, "name", e.target.value)} required />
                <Input label="الكمية المضافة" type="number" value={book.quantity} onChange={(e) => handleBookChange(i, "quantity", e.target.value)} />
                <button type="button" onClick={() => handleRemoveBookField(i)} className="mb-1 text-gray-400 hover:text-red-500 transition-colors" aria-label="Remove Item"><XCircleIcon className="h-6 w-6" /></button>
              </div>
            ))}
          </div>

          <Button type="button" onClick={handleAddBookField} variant="ghost" className="text-blue-600 flex items-center gap-1"><PlusIcon className="h-4 w-4" /> إضافة نوع آخر</Button>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => setIsEditModalOpen(false)}>إلغاء</Button>
            <Button type="submit" className="bg-blue-600 hover:bg-blue-700">حفظ التعديلات</Button>
          </div>
        </form>
      </Modal>

      {/* Export Stock Modal */}
      <Modal isOpen={isExportModalOpen} onClose={() => setIsExportModalOpen(false)} title="📦 إخراج كتب">
        <form onSubmit={handleExportStock} className="space-y-4">
          <p className="text-sm text-gray-600">أدخل الكتب والكميات التي تريد إخراجها من المخزون.</p>
          <div className="space-y-3">
            {books.map((book, i) => (
              <div key={i} className="flex gap-2 items-end">
                <Input label="اسم الكتاب" value={book.name} onChange={(e) => handleBookChange(i, "name", e.target.value)} required />
                <Input label="الكمية المخرجة"type="number"value={book.quantity}onChange={(e) => handleBookChange(i, "quantity", e.target.value)}/>
                <button type="button" onClick={() => handleRemoveBookField(i)} className="mb-1 text-gray-400 hover:text-red-500 transition-colors" aria-label="Remove Item"><XCircleIcon className="h-6 w-6" /></button>
              </div>
            ))}
          </div>

          <Button type="button" onClick={handleAddBookField} variant="ghost" className="text-blue-600 flex items-center gap-1"><PlusIcon className="h-4 w-4" /> إضافة نوع آخر</Button>

          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button type="button" variant="secondary" onClick={() => setIsExportModalOpen(false)}>إلغاء</Button>
            <Button type="submit" className="bg-yellow-600 hover:bg-yellow-700">تأكيد الإخراج</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BooksManagement;
